/**
 * React Query bindings for the campaign module, on the `campaign` function (v2). They replace
 * `hooks/use-batch-campaigns.ts`, which read and wrote the campaign tables straight from the browser.
 *
 * The list keeps its old key, `["batch-campaigns", workspaceId]`: deleting an agent invalidates it
 * (assistant.query.ts), since a campaign names its agent.
 */

import { keepPreviousData, type Query, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import {
  type Campaign,
  type CampaignAction,
  type CampaignDetail,
  type CampaignPatch,
  type ContactsQuery,
  createCampaignFromFile,
  getCampaign,
  getCampaignContactStats,
  listAllCampaigns,
  listCampaignContacts,
  type NewCampaign,
  retryImport,
  runCampaignAction,
  updateCampaign,
} from "./campaign.service";

export const campaignKeys = {
  list: (workspaceId?: string) => ["batch-campaigns", workspaceId] as const,
  detail: (id?: string) => ["campaign", id] as const,
  contacts: (id?: string, query?: ContactsQuery) => ["campaign-contacts", id, query] as const,
  stats: (id?: string) => ["campaign-contact-stats", id] as const,
};

/** A campaign whose contacts are still on their way in: the screens poll it quickly. */
export const importOpen = (campaign: Campaign) =>
  campaign.import !== null && campaign.import.status !== "done" && campaign.import.status !== "failed";

/** Statuses the backend moves on its own (dormant → warmup → in-progress): the screens poll them too. */
const ACTIVE = new Set<Campaign["status"]>(["warmup", "in-progress", "dormant"]);

/** How often to look again: every 3 s while an import runs, every 10 s while a campaign dials, else never. */
export function pollEvery(campaigns: Campaign[]): number | false {
  if (campaigns.some(importOpen)) return 3_000;
  if (campaigns.some((campaign) => ACTIVE.has(campaign.status))) return 10_000;
  return false;
}

/** Every campaign of the workspace, newest first. */
export const useCampaigns = (workspaceId?: string) =>
  useQuery<Campaign[]>({
    queryKey: campaignKeys.list(workspaceId),
    queryFn: () => listAllCampaigns(workspaceId!),
    enabled: !!workspaceId,
    refetchInterval: (query: Query<Campaign[]>) => pollEvery(query.state.data ?? []),
  });

/** One campaign, with its import's progress and the lines it skipped. */
export const useCampaign = (id?: string) =>
  useQuery<CampaignDetail>({
    queryKey: campaignKeys.detail(id),
    queryFn: () => getCampaign(id!),
    enabled: !!id,
    refetchInterval: (query: Query<CampaignDetail>) => pollEvery(query.state.data ? [query.state.data] : []),
  });

/** A page of a campaign's contacts; the page on screen stays while the next one loads. */
export const useCampaignContacts = (id: string | undefined, query: ContactsQuery) =>
  useQuery({
    queryKey: campaignKeys.contacts(id, query),
    queryFn: () => listCampaignContacts(id!, query),
    enabled: !!id,
    placeholderData: keepPreviousData,
  });

export const useCampaignContactStats = (id?: string) =>
  useQuery({
    queryKey: campaignKeys.stats(id),
    queryFn: () => getCampaignContactStats(id!),
    enabled: !!id,
  });

/** Whatever changed a campaign: its list, itself, its contacts and their counts read again. */
function useRefresh() {
  const queryClient = useQueryClient();
  return (campaign: { id: string; workspace_id: string }) => {
    queryClient.invalidateQueries({ queryKey: campaignKeys.list(campaign.workspace_id) });
    queryClient.invalidateQueries({ queryKey: campaignKeys.detail(campaign.id) });
    queryClient.invalidateQueries({ queryKey: ["campaign-contacts", campaign.id] });
    queryClient.invalidateQueries({ queryKey: campaignKeys.stats(campaign.id) });
  };
}

/** The file up, then the campaign; `onProgress` hears the upload's percent. The dialog says what happened. */
export const useCreateCampaign = () => {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: ({ settings, file, onProgress }: {
      settings: NewCampaign;
      file: { name: string; ndjson: string; contacts: number };
      onProgress: (percent: number) => void;
    }) => createCampaignFromFile(settings, file, onProgress),
    onSuccess: refresh,
  });
};

export const useUpdateCampaign = () => {
  const refresh = useRefresh();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: CampaignPatch }) => updateCampaign(id, patch),
    onSuccess: refresh,
    onError: (error: Error) => toast({ variant: "destructive", title: "Failed to update campaign", description: error.message }),
  });
};

const ACTION_DONE: Record<CampaignAction, string> = {
  start: "Campaign started",
  pause: "Campaign paused",
  resume: "Campaign resumed",
  cancel: "Campaign cancelled",
};

export const useCampaignAction = () => {
  const refresh = useRefresh();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: CampaignAction }) => runCampaignAction(id, action),
    onSuccess: (campaign, { action }) => {
      refresh(campaign);
      toast({ title: ACTION_DONE[action], description: "Campaign status updated successfully." });
    },
    onError: (error: Error) => toast({ variant: "destructive", title: "Campaign action failed", description: error.message }),
  });
};

export const useRetryImport = () => {
  const refresh = useRefresh();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (id: string) => retryImport(id),
    onSuccess: (campaign) => {
      refresh(campaign);
      toast({ title: "Import queued again", description: "The contacts import picks up from where it stopped." });
    },
    onError: (error: Error) => toast({ variant: "destructive", title: "Retry failed", description: error.message }),
  });
};
